import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe, RefreshCw, ShieldAlert, Pencil } from 'lucide-react'
import { api, ApiError } from '../api'
import type { Brand, Role, BrandContext as Ctx } from '../types'
import { atLeast } from '../types'
import { useGeneration, statusLabel } from '../tasks'
import { go } from '../router'
import {
  Button, Card, CardTitle, EmptyState, Field, Input, Textarea, Note, SectionLabel,
  ErrorText, Skeleton,
} from '../components/ui'

// A workspace holds exactly ONE brand. The workspace is the product: its
// avatars, looks, templates and ads all belong to it, and a second product
// means a second workspace. So this is an editor, not a list.

const toLines = (l: string[] | null | undefined) => (l ?? []).join('\n')
const fromLines = (s: string) => s.split('\n').map(x => x.trim()).filter(Boolean)
const withoutProtocol = (value: string) => value.replace(/^https?:\/\//i, '')
const websiteURL = (value: string) => {
  const rest = withoutProtocol(value.trim())
  return rest ? `https://${rest}` : ''
}

function WebsiteInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="flex h-9.5 overflow-hidden rounded-[--radius-control] border border-line bg-surface-2 transition-colors focus-within:border-brand focus-within:ring-2 focus-within:ring-brand/25">
      <span className="flex items-center border-r border-line bg-surface-3/50 px-3 text-sm text-ink-faint">
        https://
      </span>
      <Input
        value={withoutProtocol(value)}
        onChange={e => onChange(websiteURL(e.target.value))}
        placeholder="example.com"
        className="h-full rounded-none border-0 bg-transparent focus:ring-0"
      />
    </div>
  )
}

type Form = {
  spoken_name: string
  url: string
  one_liner: string
  what_it_does: string
  audience: string
  key_benefits: string
  avoid: string
}

const formOf = (b: Brand): Form => ({
  spoken_name: b.spoken_name,
  url: websiteURL(b.url ?? ''),
  one_liner: b.one_liner ?? '',
  what_it_does: b.what_it_does ?? '',
  audience: b.audience ?? '',
  key_benefits: toLines(b.key_benefits),
  avoid: toLines(b.avoid),
})

export function BrandContext({ ws, role, startEditing = false }: {
  ws: string
  role?: Role
  startEditing?: boolean
}) {
  const brand = useQuery({
    queryKey: ['brand', ws],
    queryFn: async () => {
      try {
        return await api.get<Brand>(`/api/workspaces/${ws}/brand`)
      } catch (e) {
        // 404 means it has not been set up yet, which is a state rather than
        // an error -- the page shows the setup form instead of a failure.
        if (e instanceof ApiError && e.status === 404) return null
        throw e
      }
    },
  })
  const canEdit = atLeast(role, 'member')

  if (brand.isLoading) {
    return <div className="space-y-4"><Skeleton className="h-48 w-full" /></div>
  }
  if (brand.error) return <ErrorText error={brand.error} />
  if (!brand.data) return <SetupBrand ws={ws} canEdit={canEdit} />
  return <Editor ws={ws} brand={brand.data} canEdit={canEdit} startEditing={startEditing} />
}

function Editor({ ws, brand, canEdit, startEditing }: {
  ws: string
  brand: Brand
  canEdit: boolean
  startEditing: boolean
}) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(startEditing && canEdit)
  const [form, setForm] = useState<Form>(() => formOf(brand))

  // Re-seed while closed, so an extraction that lands in the background does
  // not leave stale values waiting in a form nobody has opened yet.
  useEffect(() => { if (!editing) setForm(formOf(brand)) }, [brand, editing])

  const set = (k: keyof Form, v: string) => setForm(f => ({ ...f, [k]: v }))

  const extract = useGeneration<Ctx>(ws, `brands/${brand.id}/extract`)
  useEffect(() => {
    if (extract.result) qc.invalidateQueries({ queryKey: ['brand', ws] })
  }, [extract.result, qc, ws])

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/api/workspaces/${ws}/brands/${brand.id}`, {
        ...form,
        key_benefits: fromLines(form.key_benefits),
        avoid: fromLines(form.avoid),
      }),
    onSuccess: () => {
      setEditing(false)
      qc.invalidateQueries({ queryKey: ['brand', ws] })
      if (startEditing) go({ name: 'getting-started' })
    },
  })

  const hasContext = !!brand.one_liner || !!brand.what_it_does || !!brand.key_benefits?.length

  return (
    <div className="space-y-5">
      <Card className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle>{brand.name}</CardTitle>
            <p className="mt-1 text-sm text-ink-dim">
              Spoken as <span className="text-ink">“{brand.spoken_name}”</span>
            </p>
            {brand.url && (
              <a
                href={brand.url} target="_blank" rel="noreferrer"
                className="mt-1 inline-flex items-center gap-1.5 text-xs text-ink-faint hover:text-brand"
              >
                <Globe className="size-3" /> {brand.url}
              </a>
            )}
          </div>

          {canEdit && !editing && (
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                <Pencil /> Edit
              </Button>
              {brand.url && (
                <Button
                  variant="secondary" size="sm"
                  onClick={() => extract.submit.mutate({})} disabled={extract.busy}
                >
                  <RefreshCw />
                  {extract.busy
                    ? statusLabel(extract.task) || 'Reading…'
                    : hasContext ? 'Re-read site' : 'Read from site'}
                </Button>
              )}
            </div>
          )}
        </div>

        {!brand.url && canEdit && !editing && (
          <Note>Add a website in Edit, and the context can be read from it automatically.</Note>
        )}
        {extract.busy && <Note tone="brand">Reading {brand.url} — safe to leave this page.</Note>}
        {extract.failed && <ErrorText error={extract.failed} />}
        <ErrorText error={extract.error} />

        {editing
          ? (
            <EditForm
              form={form} set={set}
              onCancel={() => { setEditing(false); setForm(formOf(brand)) }}
              onSave={() => save.mutate()}
              saving={save.isPending}
              error={save.error}
            />
          )
          : hasContext
            ? <ReadView brand={brand} />
            : (
              <EmptyState
                icon={Globe}
                title="No brand context yet"
                body="Read it from the website, or fill it in by hand. Without it, drafted scripts would be guesswork."
              />
            )}
      </Card>

    </div>
  )
}

function ReadView({ brand }: { brand: Brand }) {
  return (
    <div className="space-y-5 border-t border-line pt-5">
      <SectionLabel>Brand Context</SectionLabel>

      {brand.one_liner && <p className="text-sm leading-relaxed">{brand.one_liner}</p>}
      {brand.what_it_does && (
        <p className="text-sm leading-relaxed text-ink-dim">{brand.what_it_does}</p>
      )}

      {brand.audience && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.08em] text-ink-faint">Audience</div>
          <div className="mt-0.5 text-sm text-ink-dim">{brand.audience}</div>
        </div>
      )}

      {!!brand.key_benefits?.length && (
        <div className="space-y-2">
          <SectionLabel>Benefits</SectionLabel>
          <ul className="space-y-1.5">
            {brand.key_benefits.map(b => (
              <li key={b} className="flex gap-2 text-sm text-ink-dim">
                <span className="mt-2 size-1 shrink-0 rounded-full bg-brand" />{b}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Shown in full rather than collapsed. These are the constraints on
          what a video may say -- worth reading every time someone opens this
          page, which a details/summary quietly discourages. */}
      {!!brand.avoid?.length && (
        <div className="space-y-2">
          <SectionLabel className="flex items-center gap-1.5 text-warn">
            <ShieldAlert className="size-3.5" /> Don’t mention
          </SectionLabel>
          <ul className="space-y-1.5">
            {brand.avoid.map(a => (
              <li key={a} className="flex gap-2 text-sm text-ink-dim">
                <span className="mt-2 size-1 shrink-0 rounded-full bg-warn" />{a}
              </li>
            ))}
          </ul>
        </div>
      )}

    </div>
  )
}

function EditForm({ form, set, onCancel, onSave, saving, error }: {
  form: Form
  set: (k: keyof Form, v: string) => void
  onCancel: () => void
  onSave: () => void
  saving: boolean
  error: unknown
}) {
  return (
    <div className="space-y-5 border-t border-line pt-5">
      <SectionLabel>Brand Context</SectionLabel>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Spoken name"
          help="How it should be READ ALOUD. A run-together name gets read as one mangled word."
        >
          <Input value={form.spoken_name} onChange={e => set('spoken_name', e.target.value)} />
        </Field>
        <Field label="Website" help="Where the context is read from.">
          <WebsiteInput value={form.url} onChange={value => set('url', value)} />
        </Field>
      </div>

      <Field label="One-liner" help="What it is, in a sentence a stranger would understand.">
        <Textarea rows={2} value={form.one_liner} onChange={e => set('one_liner', e.target.value)} />
      </Field>

      <Field label="What it does" help="Two or three sentences on what it actually does day to day.">
        <Textarea rows={3} value={form.what_it_does} onChange={e => set('what_it_does', e.target.value)} />
      </Field>

      <Field label="Audience">
        <Input value={form.audience} onChange={e => set('audience', e.target.value)} />
      </Field>

      <Field label="Benefits" help="One per line. Concrete outcomes, in your customers' words.">
        <Textarea
          rows={5} value={form.key_benefits}
          onChange={e => set('key_benefits', e.target.value)}
          placeholder={'Schedules a month of posts in minutes\nGenerates captions and images'}
        />
      </Field>

      <Field
        label="Don’t mention"
        help="One per line. Add topics, features, claims or phrases that scripts should leave out. Re-reading the website will not overwrite this list."
      >
        <Textarea
          rows={5} value={form.avoid}
          onChange={e => set('avoid', e.target.value)}
          placeholder={'Enterprise plan\nCompetitor comparisons'}
          className="border-warn/25"
        />
      </Field>

      <ErrorText error={error} />
      <div className="flex gap-2">
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save context'}
        </Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  )
}

function SetupBrand({ ws, canEdit }: { ws: string; canEdit: boolean }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [spoken, setSpoken] = useState('')
  const [url, setUrl] = useState('')

  const create = useMutation({
    mutationFn: () => api.post(`/api/workspaces/${ws}/brands`, {
      name, spoken_name: spoken || name, url: websiteURL(url) || null,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['brand', ws] }),
  })

  if (!canEdit) {
    return (
      <EmptyState
        icon={Globe}
        title="No brand set up"
        body="This workspace has no brand yet. Ask an admin to add one."
      />
    )
  }

  return (
    <Card className="space-y-4 border-brand/25">
      <div>
        <CardTitle>Set up this workspace's brand</CardTitle>
        <p className="mt-1 text-sm text-ink-faint">
          A workspace holds one product. A second product means a second workspace.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Name">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="ContentStudio" />
        </Field>
        <Field
          label="Spoken name"
          help="Add spaces where needed so the voice pronounces each word clearly."
        >
          <Input value={spoken} onChange={e => setSpoken(e.target.value)} placeholder="Content Studio" />
        </Field>
      </div>

      <Field label="Website">
        <WebsiteInput value={url} onChange={setUrl} />
      </Field>

      <ErrorText error={create.error} />
      <Button variant="primary" onClick={() => create.mutate()} disabled={!name || create.isPending}>
        {create.isPending ? 'Saving…' : 'Create brand'}
      </Button>
    </Card>
  )
}
